import { useRecaptcha } from '@cosign-services/hooks/useRecaptcha'
import { datadogRum } from '@datadog/browser-rum'
import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { redactWithSizeCap } from '@dni-api-client/datadog/redact'
import { isEmail } from '@utils'
import { type AxiosError } from 'axios'
import { useCallback } from 'react'
import { useContextCosign } from '../components/useContextCosign'
import { type Otp } from '../types/dataModel/platformSdk'
import { resolveLineInvitationRedirectUrl } from '../utils/lineInvitationUrl'
import { useRequestSendOtp } from './apis/wp/useRequestSendOtp'
import { useCosignCookies } from './useCosignCookies'
import { useLineInvitationCheck } from './useLineInvitationCheck'
import { useRedirectUrl } from './useRedirectUrl'

interface UseOtpCosignSendProps {
  tokenLoginUrl?: Otp['tokenLoginUrl']
  redirectUrl?: string
  fallbackUrl?: string
}

export function useOtpCosignSend(props?: UseOtpCosignSendProps) {
  const { tokenLoginUrl } = props ?? {}
  const { lineInvitation } = useContextCosign()
  const { account, countryCode, country, local } = useCosignCookies()
  const { trigger: sentOtp, isMutating, data } = useRequestSendOtp()
  const { executeRecaptcha } = useRecaptcha({ siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY })
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const { redirectUrl, validateUrl } = useRedirectUrl()

  const lineInvitationCheck = useLineInvitationCheck({
    account,
    enabled: lineInvitation,
  })

  const sendOtp = useCallback(async (props: { type: Otp['type'], redirectUrl: string, purpose: Otp['purpose'], fallbackUrl?: Otp['fallbackUrl'], validateUrl: Otp['validateUrl'], tokenLoginUrl?: Otp['tokenLoginUrl'] }) => {
    try {
      const { type } = props
      if (!account) throw new Error('account is required')
      if (type === 'email' && !isEmail(account)) throw new Error('account is email')
      if (type === 'sms' && !countryCode) throw new Error('if type is sms, countryCode is required')
      // NOTE: 為了解決 country cookie 會不一致問題，遇到 US 的手機都送英文的 OTP template
      const isUsPhone = type === 'sms' && account.startsWith('+1')
      const recaptchaToken = await executeRecaptcha('login')
      const { data } = await sentOtp({
        account,
        countryCode,
        otpTemplateSettingKey: isUsPhone ? 'US_en_US' : `${country}_${local}`,
        recaptchaToken,
        ...props,
      })
      if (type === 'sms') sendEvent({ eventName: 'mobile OTP', eventParams: { event_category: 'Mobile OTP', mobile_otp_receive_result: true } })
      if (type === 'email') sendEvent({ eventName: 'Email OTP', eventParams: { event_category: 'Email OTP', email_otp_receive_result: true } })
      return data
    }
    catch (e) {
      const { type, purpose } = props
      if (type === 'sms') sendEvent({ eventName: 'mobile OTP', eventParams: { event_category: 'Mobile OTP', mobile_otp_receive_result: false } })
      if (type === 'email') sendEvent({ eventName: 'Email OTP', eventParams: { event_category: 'Email OTP', email_otp_receive_result: false } })
      try {
        const axiosError = e as AxiosError
        const responseData = axiosError.response?.data
        datadogRum.addError(e, {
          function: 'useOtpCosignSend.sendOtp',
          api: {
            key: '/api/platform-sdk/otp',
            status: axiosError.response?.status,
            requestBody: redactWithSizeCap({ type, purpose, countryCode }),
            responseData: responseData === undefined ? undefined : redactWithSizeCap(responseData),
          },
        })
      } catch {
        /* 觀測失敗不影響流程，靜默吞掉 */
      }
      throw e
    }
  }, [account, country, countryCode, executeRecaptcha, local, sendEvent, sentOtp])

  const loginSendOtp = useCallback((type: Otp['type']) => {
    const targetUrl = props?.redirectUrl || redirectUrl.registerOrLogin
    const finalRedirectUrl = lineInvitation
      ? resolveLineInvitationRedirectUrl(targetUrl, lineInvitationCheck)
      : targetUrl

    return sendOtp({
      type,
      redirectUrl: finalRedirectUrl,
      fallbackUrl: props?.fallbackUrl || redirectUrl.registerOrLogin,
      purpose: 'login',
      validateUrl: validateUrl.registerOrLogin,
      tokenLoginUrl,
    })
  }, [lineInvitationCheck, props?.fallbackUrl, props?.redirectUrl, redirectUrl.registerOrLogin, sendOtp, tokenLoginUrl, validateUrl.registerOrLogin, lineInvitation])

  const bindSendOtp = useCallback((type: Otp['type']) => sendOtp({
    type,
    redirectUrl: props?.redirectUrl || `${redirectUrl.bind}?${type}Binding=true`,
    fallbackUrl: props?.fallbackUrl || `${redirectUrl.bind}?${type}Binding=false`,
    purpose: 'bind',
    validateUrl: validateUrl.bind,
    tokenLoginUrl,
  }), [props?.fallbackUrl, props?.redirectUrl, redirectUrl.bind, sendOtp, tokenLoginUrl, validateUrl.bind])

  const forgetPasswordSendOtp = useCallback((type: Otp['type']) => sendOtp({
    type,
    redirectUrl: props?.redirectUrl || redirectUrl.forgetPassword,
    fallbackUrl: props?.fallbackUrl || redirectUrl.forgetPassword,
    purpose: 'forget',
    validateUrl: validateUrl.forgetPassword,
    tokenLoginUrl,
  }), [props?.fallbackUrl, props?.redirectUrl, redirectUrl.forgetPassword, sendOtp, tokenLoginUrl, validateUrl.forgetPassword])

  return {
    loginSendOtp,
    bindSendOtp,
    forgetPasswordSendOtp,
    isMutating,
    data,
  }
}
